(ns practitest.frontend.components.view-editor.events
  (:require
    [clojure.string :as str]
    [goog.functions :as gfunc]
    [practitest.frontend.common-entities.events :as common-events]
    [practitest.frontend.common-entities.metadata :as md]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.common.re-frame :as rf-common]
    [practitest.frontend.components.view-editor.basic-builder :as bb]
    [practitest.frontend.components.view-editor.schema :refer [event-interceptors]]
    [practitest.frontend.events :as events]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

;; ============================================================================
;; View editor - modal for creating / editing custom views.
;; ============================================================================
;;
;; A view has two filter modes:
;;   - `:ptql`           free-form PTQL text. Has two render modes:
;;                         `:advanced`: CodeMirror editor
;;                         `:basic`:    clauses + AND/OR groups ('basic_builder.cljs')
;;   - `:auto-filter`    pick fields, the tree renders hierarchy of values
;;
;; Editing flow:
;;   1. open-edit / open-create: fetch editor data, populate form
;;   2. user edits: `:form` mutations + debounced `::recompute!` to refresh
;;      combined PTQL preview from backend
;;   3. save: on success activate the saved view
;;
;; Basic-builder <-> PTQL roundtrip:
;;   - basic edits update `:basic-builder-ast` -> `::emit-ptql-and-recompute!`
;;     emits PTQL text -> `::view-ptql-changed` updates `:view-ptql` ->
;;     debounced `::recompute!` refreshes server-derived previews.
;;   - advanced->basic: rebuild basic AST from server's `view_ast`

;; ---- helpers ----

(def ^:private trigger-recompute!
  (gfunc/debounce
    #(re-frame/dispatch [::recompute!])
    400))

;; ---- effects ----

(re-frame/reg-fx
  ::trigger-recompute-debounced
  (fn [_]
    (trigger-recompute!)))

(re-frame/reg-fx
  ::emit-ptql-and-recompute!
  (fn [{:keys [ast entity fields-by-entity cross-filter-views]}]
    (let [source-fn (bb/display-name-fn-for fields-by-entity entity)
          linked-fn (partial bb/display-name-fn-for fields-by-entity)
          ptql (or (bb/ast->ptql source-fn linked-fn cross-filter-views ast) "")]
      (re-frame/dispatch [::view-ptql-changed ptql]))))

;; ---- events ----

;; -- lifecycle --

(re-frame/reg-event-fx
  ::open-edit
  event-interceptors
  (fn [{db :db} [_ view-id]]
    (let [project-id (subs/get-current-project-id db)]
      {:db (assoc db :view-editor {:open? true
                                   :mode :edit
                                   :view-id view-id
                                   :loading? true})
       ::rf-common/http {:url (broutes/get-url "custom_views#editor_data"
                                               {:project_id project-id :id view-id})
                         :on-success [::loaded]
                         :on-failure [::load-failed]}})))

(re-frame/reg-event-fx
  ::open-create
  event-interceptors
  (fn [{db :db} [_ {:keys [parent-id entity-type seed]}]]
    (let [project-id (subs/get-current-project-id db)]
      {:db (assoc db :view-editor {:open? true
                                   :mode :create
                                   :parent-id parent-id
                                   :entity-type entity-type
                                   :seed seed
                                   :loading? true})
       ::rf-common/http {:url (broutes/get-url "custom_views#new_editor_data"
                                               {:project_id project-id}
                                               {:parent_id parent-id :entity_type entity-type})
                         :on-success [::loaded]
                         :on-failure [::load-failed]}})))

;; Hydrate `:form` from server response.
;; If `::open-create` was called with a `:seed` (e.g. assistant pre-filling view-ptql),
;; seed values override loaded ones, and we kick off `::recompute!`
;; so the basic-builder AST gets rebuilt from the seeded ptql.
(re-frame/reg-event-fx
  ::loaded
  event-interceptors
  (fn [{db :db} [_ data]]
    (let [seed (get-in db [:view-editor :seed])
          base-form {:name (or (:name data) "")
                     :public? (boolean (:is_public data))
                     :definition (vec (:definition data))
                     :mode (case (:mode data)
                             "auto_filter" :auto-filter
                             :ptql)
                     :ancestors-path (vec (:ancestors_path data))
                     :ancestors-ptql-raw (:ancestors_ptql_raw data)
                     :ancestors-ptql-optimized (:ancestors_ptql_optimized data)
                     :view-ptql (or (:view_ptql data) "")
                     :combined-ptql-raw (:combined_ptql_raw data)
                     :combined-ptql-optimized (:combined_ptql_optimized data)
                     :view-ast (:view_ast data)
                     :basic-builder-ast (bb/wrap-as-group (:view_ast data))
                     :auto-filter (vec (:auto_filter data))
                     :hide-empty-auto-filters (boolean (:hide_empty_auto_filters data))}
          new-db (-> db
                     (update :view-editor merge
                             {:loading? false
                              :entity-type (:entity_type data)
                              :parent-id (:parent_id data)
                              :filter-render-mode (if (str/blank? (:view-ptql seed))
                                                    (if (bb/fits? (:view_ast data)) :basic :advanced)
                                                    :advanced)
                              ;; XXX: do we really need to return the auto-filter options
                              ;;      (list of fields that can be used as auto-filter fields)
                              ;;      we have all the fields in frontend
                              :auto-filter-options (vec (:auto_filter_options data))
                              :auto-filter-max-levels (:auto_filter_max_levels data)
                              :auto-filter-limit-reached? (boolean (:auto_filter_limit_reached data))
                              :available-fields (vec (:available_fields data))
                              :cross-filter-modules (vec (:cross_filter_modules data))
                              :cross-filter-urls (events/stringify-keys (:cross_filter_urls data))
                              :cross-filter-views (events/stringify-keys (:cross_filter_views data))
                              :form (merge base-form seed)})
                     (update :view-editor dissoc :seed))]
      (cond-> {:db new-db}
        (not (str/blank? (:view-ptql seed)))
        (assoc :dispatch [::recompute! {:rebuild-basic? true :auto-promote-basic? true}])))))

(re-frame/reg-event-db
  ::load-failed
  event-interceptors
  (fn [db _]
    (update db :view-editor merge
            {:loading? false
             :error (get-translation "view-editor.error.load-failed"
                                     "Failed to load view data")})))

(re-frame/reg-event-db
  ::close
  event-interceptors
  (fn [db _]
    (assoc db :view-editor {:open? false})))

;; -- form fields --

(re-frame/reg-event-db
  ::name-changed
  event-interceptors
  (fn [db [_ name]]
    (-> db
        (assoc-in [:view-editor :form :name] name)
        (assoc-in [:view-editor :form :name-auto-generated?] false))))

(re-frame/reg-event-db
  ::public-changed
  event-interceptors
  (fn [db [_ value]]
    (assoc-in db [:view-editor :form :public?] value)))

(re-frame/reg-event-db
  ::set-definition
  event-interceptors
  (fn [db [_ definition]]
    (assoc-in db [:view-editor :form :definition] definition)))

(re-frame/reg-event-db
  ::set-mode
  event-interceptors
  (fn [db [_ mode]]
    (assoc-in db [:view-editor :form :mode] mode)))

;; -- ptql / basic-builder filter --

;; advanced -> basic needs to rebuild the basic AST from current view-ptql
;; (server parses it during `::recompute!`).
;; basic -> advanced needs nothing - the CodeMirror editor is already populated.
(re-frame/reg-event-fx
  ::set-filter-render-mode
  event-interceptors
  (fn [{db :db} [_ render-mode]]
    (let [current-mode (get-in db [:view-editor :filter-render-mode])
          syncing? (and (= :basic render-mode)
                        (= :advanced current-mode))]
      (cond-> {:db (assoc-in db [:view-editor :filter-render-mode] render-mode)}
        syncing? (assoc :dispatch [::recompute! {:rebuild-basic? true}])))))

(defn- apply-ast-update [db update-fn]
  (let [old-ast (get-in db [:view-editor :form :basic-builder-ast])
        new-ast (update-fn old-ast)
        valid (bb/valid-ast new-ast)
        entity (get-in db [:view-editor :entity-type])]
    {:db (assoc-in db [:view-editor :form :basic-builder-ast] new-ast)
     ::emit-ptql-and-recompute! {:ast valid
                                 :entity entity
                                 :fields-by-entity (subs/fields-by-ptql-entity (:fields db) (:entity-fields db))
                                 :cross-filter-views (get-in db [:view-editor :cross-filter-views])}}))

(re-frame/reg-event-fx
  ::set-clause-field
  event-interceptors
  (fn [{db :db} [_ path field-id field-type]]
    (apply-ast-update db #(bb/set-clause-field % path field-id field-type))))

(re-frame/reg-event-fx
  ::set-clause-op
  event-interceptors
  (fn [{db :db} [_ path op-code]]
    (apply-ast-update db #(bb/update-at % path bb/change-op op-code))))

(re-frame/reg-event-fx
  ::set-clause-value
  event-interceptors
  (fn [{db :db} [_ path value]]
    (apply-ast-update db #(bb/update-at % path bb/assoc-clause-value value))))

(re-frame/reg-event-fx
  ::set-group-combinator
  event-interceptors
  (fn [{db :db} [_ path combinator]]
    (apply-ast-update db #(bb/update-at % path (fn [g] (assoc g 0 (name combinator)))))))

(re-frame/reg-event-fx
  ::add-clause
  event-interceptors
  (fn [{db :db} [_ path]]
    (apply-ast-update db #(bb/append-child % path (bb/empty-clause)))))

(re-frame/reg-event-fx
  ::add-cross-filter
  event-interceptors
  (fn [{db :db} [_ path]]
    (apply-ast-update db #(bb/append-child % path (bb/empty-cross-filter)))))

(re-frame/reg-event-fx
  ::toggle-cross-filter-negation
  event-interceptors
  (fn [{db :db} [_ path]]
    (apply-ast-update db #(bb/update-at % path bb/negate-clause))))

(re-frame/reg-event-fx
  ::set-cross-filter-entity
  event-interceptors
  (fn [{db :db} [_ path entity]]
    (let [db' (assoc-in db [:view-editor :form :cross-filter-entity-by-path path] entity)]
      (apply-ast-update db' (fn [ast]
                              (bb/update-at ast path
                                            (fn [clause]
                                              (cond-> (bb/empty-cross-filter)
                                                (bb/not-node? clause) (bb/negate-clause)))))))))

(re-frame/reg-event-fx
  ::set-cross-filter-view
  event-interceptors
  (fn [{db :db} [_ path server-obj]]
    (let [view-id (some-> server-obj :id (js/parseInt 10))
          entity (or (:entity server-obj)
                     (get-in db [:view-editor :form :cross-filter-entity-by-path path])
                     ;; XXX: is this good enough?
                     (first (get-in db [:view-editor :cross-filter-modules])))
          view-ref (cond-> {:view_id view-id}
                     (:auto_filter server-obj) (assoc :auto_filter (:auto_filter server-obj))
                     (:field server-obj) (assoc :field (:field server-obj)))
          db' (-> db
                  (update-in [:view-editor :cross-filter-views]
                             assoc (str view-id) {:name (:name server-obj) :entity entity}))]
      (apply-ast-update db'
                        (fn [ast]
                          (bb/update-at ast path
                                        (fn [clause]
                                          (cond-> (bb/build-cross-filter view-ref)
                                            (bb/not-node? clause) (bb/negate-clause)))))))))

(re-frame/reg-event-fx
  ::add-nested-group
  event-interceptors
  (fn [{db :db} [_ path]]
    (apply-ast-update db #(bb/append-child % path (bb/empty-group)))))

(re-frame/reg-event-fx
  ::add-group
  event-interceptors
  (fn [{db :db} [_ path]]
    (apply-ast-update db #(bb/add-group-at % path))))

(re-frame/reg-event-fx
  ::remove-at
  event-interceptors
  (fn [{db :db} [_ path]]
    (apply-ast-update db #(bb/remove-at % path))))

(re-frame/reg-event-fx
  ::view-ptql-changed
  event-interceptors
  (fn [{db :db} [_ ptql]]
    {:db (assoc-in db [:view-editor :form :view-ptql] ptql)
     ::trigger-recompute-debounced true}))

(re-frame/reg-event-db
  ::set-ptql-error
  event-interceptors
  (fn [db [_ error]]
    (assoc-in db [:view-editor :ptql-error] error)))

;; -- auto-filter --

(defn- replace-auto-filter-at [af idx value]
  (cond
    (str/blank? value) (vec (take idx af))
    (< idx (count af)) (assoc (vec af) idx value)
    :else (conj (vec af) value)))

(defn- auto-filter-name [labels]
  (str (get-translation "view-editor.auto-filter.name-prefix" "Auto Filter by")
       " " (str/join " / " labels)))

(re-frame/reg-event-db
  ::set-auto-filter-field
  event-interceptors
  (fn [db [_ idx value]]
    (let [form (get-in db [:view-editor :form])
          new-af (replace-auto-filter-at (or (:auto-filter form) []) idx value)
          rename? (and (seq new-af)
                       (or (:name-auto-generated? form)
                           (str/blank? (:name form))))
          opts-by-val (into {} (map (juxt :value :label))
                            (get-in db [:view-editor :auto-filter-options]))]
      (cond-> (assoc-in db [:view-editor :form :auto-filter] new-af)
        rename? (-> (assoc-in [:view-editor :form :name] (auto-filter-name (mapv #(opts-by-val % %) new-af)))
                    (assoc-in [:view-editor :form :name-auto-generated?] true))))))

(re-frame/reg-event-db
  ::add-auto-filter-level
  event-interceptors
  (fn [db _]
    (update-in db [:view-editor :form :auto-filter]
               (fn [af]
                 (conj (vec af) nil)))))

(re-frame/reg-event-db
  ::remove-auto-filter-level
  event-interceptors
  (fn [db [_ idx]]
    (update-in db [:view-editor :form :auto-filter]
               (fn [af]
                 (vec (take idx (or af [])))))))

(re-frame/reg-event-db
  ::set-hide-empty-auto-filters
  event-interceptors
  (fn [db [_ value]]
    (assoc-in db [:view-editor :form :hide-empty-auto-filters] value)))

;; -- recompute (server-side ptql derivation) --

(re-frame/reg-event-fx
  ::recompute!
  event-interceptors
  (fn [{db :db} [_ {:keys [rebuild-basic? auto-promote-basic?]}]]
    (let [project-id (subs/get-current-project-id db)
          {:keys [parent-id entity-type]} (:view-editor db)
          view-ptql (get-in db [:view-editor :form :view-ptql])]
      {:db (assoc-in db [:view-editor :recomputing?] true)
       ::rf-common/http {:url (broutes/get-url "custom_views#recompute_combined"
                                               {:project_id project-id})
                         :method "POST"
                         :body {:parent_id parent-id
                                :entity_type entity-type
                                :view_ptql view-ptql}
                         :on-success [::recomputed {:rebuild-basic? rebuild-basic?
                                                    :auto-promote-basic? auto-promote-basic?}]
                         :on-failure [::recompute-failed]}})))

;; Server returned the parsed AST + raw/optimized ptql for the current view.
;; Flags:
;;   `:rebuild-basic?`: overwrite `:basic-builder-ast`
;;                      (used after CodeMirror edit)
;;   `:auto-promote-basic?`: if AST fits basic mode, switch to it
;;                           (used during `::loaded` with a seeded ptql)
(re-frame/reg-event-db
  ::recomputed
  event-interceptors
  (fn [db [_
           {:keys [rebuild-basic? auto-promote-basic?]}
           {:keys [view_ast combined_ptql_raw combined_ptql_optimized]}]]
    (cond-> (-> db
                (assoc-in [:view-editor :recomputing?] false)
                (assoc-in [:view-editor :form :view-ast] view_ast)
                (assoc-in [:view-editor :form :combined-ptql-raw] combined_ptql_raw)
                (assoc-in [:view-editor :form :combined-ptql-optimized] combined_ptql_optimized))
      rebuild-basic?
      (assoc-in [:view-editor :form :basic-builder-ast] (bb/wrap-as-group view_ast))

      (and auto-promote-basic? (bb/fits? view_ast))
      (assoc-in [:view-editor :filter-render-mode] :basic))))

(re-frame/reg-event-db
  ::recompute-failed
  event-interceptors
  (fn [db _]
    ;; XXX: show error in UI?
    (assoc-in db [:view-editor :recomputing?] false)))

;; -- parent filter --

(re-frame/reg-event-fx
  ::set-parent-filter
  event-interceptors
  (fn [{db :db} [_ server-obj]]
    (let [id-str     (some-> (:id server-obj) str)
          parsed     (when (and id-str (not= id-str "_DEFAULT_"))
                       (js/parseInt id-str 10))
          new-parent-id (when (and parsed (not (js/isNaN parsed))) parsed)
          ancestors-path (if new-parent-id
                           (conj (vec (:ancestors_path server-obj))
                                 {:id new-parent-id :name (or (:name server-obj) "")})
                           [])]
      {:db (-> db
               (assoc-in [:view-editor :parent-id] new-parent-id)
               (assoc-in [:view-editor :form :ancestors-path] ancestors-path)
               (assoc-in [:view-editor :form :ancestors-ptql-optimized] nil)
               (assoc-in [:view-editor :form :ancestors-ptql-raw] nil))
       :dispatch [::recompute!]})))

;; -- save --

(re-frame/reg-event-fx
  ::save
  event-interceptors
  (fn [{db :db} _]
    (let [project-id (subs/get-current-project-id db)
          {:keys [mode view-id parent-id entity-type]} (:view-editor db)
          form (get-in db [:view-editor :form])
          create? (= mode :create)
          auto-filter-mode? (= :auto-filter (:mode form))
          base-body (cond-> {:name (:name form)
                             :is_public (boolean (:public? form))
                             :parent_id parent-id
                             :definition (vec (:definition form))}
                      create? (assoc :entity_type entity-type))
          body (if auto-filter-mode?
                 (assoc base-body
                        :auto_filter (vec (remove str/blank? (:auto-filter form)))
                        :hide_empty_auto_filters (boolean (:hide-empty-auto-filters form)))
                 (assoc base-body :ptql (:view-ptql form)))]
      {:db (assoc-in db [:view-editor :saving?] true)
       ::rf-common/http {:url (broutes/get-url
                                (if create? "custom_views#create_view_editor" "custom_views#update_view_editor")
                                (cond-> {:project_id project-id}
                                  (not create?) (assoc :id view-id)))
                         :method (if create? "POST" "PATCH")
                         :body body
                         :on-success [::saved]
                         :on-failure [::save-failed]}})))

;; Server returns HTTP 200 with `{:status "error"}` for validation errors
;; (duplicate name, etc.) - handled here, not in `::save-failed`.
(re-frame/reg-event-fx
  ::saved
  event-interceptors
  (fn [{db :db} [_ resp]]
    (if (= (:status resp) "ok")
      (let [entity-type (get-in db [:view-editor :entity-type])
            entity-kw (md/entity-by-module entity-type)
            saved-id (:id resp)]
        {:db (-> db
                 (assoc :view-editor {:open? false})
                 (assoc :new-filter-id (str saved-id "-" (.getTime (js/Date.)))))
         :dispatch [::common-events/activate-view entity-kw saved-id]})
      {:db (update db :view-editor merge
                   {:saving? false
                    :error (or (:message resp) "Save failed")})})))

(re-frame/reg-event-db
  ::save-failed
  event-interceptors
  (fn [db [_ resp]]
    (update db :view-editor merge
            {:saving? false
             :error (or (:message resp)
                        (get-translation "view-editor.error.save-failed"
                                         "Failed to save"))})))
